/**
 * Region-aware host resolution for the widget.
 *
 * Gleap runs one stack per data region. Every regional stack exposes the same
 * set of service labels in front of a regional suffix:
 *
 *   EU   api.gleap.io        dashapi.gleap.io        sockets.gleap.io        staticfiles.gleap.io        files.gleap.io
 *   EU   api.eu.gleap.ai     dashapi.eu.gleap.ai     sockets.eu.gleap.ai     staticfiles.eu.gleap.ai     files.eu.gleap.ai   (alias)
 *   US   api.us.gleap.ai     dashapi.us.gleap.ai     sockets.us.gleap.ai     staticfiles.us.gleap.ai     files.us.gleap.ai
 *
 * The host SDK only tells the widget its `apiUrl` (the JS SDK optionally adds
 * `realtimeHost`; the native iOS / Android SDKs never do), so everything else
 * is derived from that one URL. Anything that is not a Gleap-owned host — a
 * customer's custom domain, localhost — is left exactly as it was handed in.
 *
 * Pure on purpose: no stores, no `process.env`, no `window`. The build default
 * is passed in by the caller so this stays trivially testable.
 */

/**
 * The suffixes Gleap owns. `gleap.io` is the legacy EU apex and carries no
 * region label; `gleap.ai` takes an optional single region label (`eu`, `us`,
 * …) and is also accepted bare.
 */
const GLEAP_SUFFIX_PATTERN = /^(?:gleap\.io|(?:[a-z0-9-]+\.)?gleap\.ai)$/;

interface SplitHost {
  label: string;
  rest: string;
}

const splitGleapHost = (hostname?: string | null): SplitHost | undefined => {
  if (typeof hostname !== "string") return undefined;

  const normalized = hostname.trim().toLowerCase();
  const dot = normalized.indexOf(".");
  if (dot <= 0) return undefined;

  const label = normalized.slice(0, dot);
  const rest = normalized.slice(dot + 1);
  if (!GLEAP_SUFFIX_PATTERN.test(rest)) return undefined;

  return { label, rest };
};

const hostnameOf = (url?: string | null): string | undefined => {
  if (typeof url !== "string" || !url) return undefined;

  try {
    return new URL(url).hostname;
  } catch (_) {
    return undefined;
  }
};

/**
 * The API base the widget should actually talk to.
 *
 * SDKs are configured with the public ingest host (`api.*`), while the widget
 * reads from the dashboard-facing one (`dashapi.*`). For a Gleap-owned `api.*`
 * host the first label is swapped; every other URL is returned untouched, so a
 * custom domain keeps pointing wherever the customer proxied it.
 *
 * The swap is done on the original string (not a re-serialised URL) so nothing
 * else about it changes — no trailing slash appears or disappears.
 */
export const resolveWidgetApiBase = <T extends string | null | undefined>(
  apiUrl: T
): T | string => {
  const host = splitGleapHost(hostnameOf(apiUrl));
  if (!apiUrl || !host || host.label !== "api") return apiUrl;

  return apiUrl.replace(/^(https?:\/\/)api\./i, "$1dashapi.");
};

/**
 * The realtime (Sockudo) host to connect to.
 *
 * 1. An explicit `realtimeHost` from the host SDK always wins.
 * 2. Otherwise a Gleap-owned `api.*` / `dashapi.*` URL maps to the `sockets.*`
 *    host of the same region — this is what gives native iOS / Android users,
 *    whose SDKs never send `realtimeHost`, live updates outside the EU.
 * 3. Otherwise (custom domain, localhost, nothing at all) the build default.
 */
export const resolveRealtimeHost = (
  realtimeHost: string | null | undefined,
  apiUrl: string | null | undefined,
  buildDefault: string
): string => {
  if (typeof realtimeHost === "string" && realtimeHost.trim()) {
    return realtimeHost.trim();
  }

  const host = splitGleapHost(hostnameOf(apiUrl));
  if (host && (host.label === "api" || host.label === "dashapi")) {
    return `sockets.${host.rest}`;
  }

  return buildDefault;
};

/**
 * The public attachment CDN of any region (`staticfiles.*`). Uploaded
 * attachments live here and Cloudflare Image Resizing is enabled on it.
 */
export const isGleapStaticFilesHost = (hostname?: string | null): boolean =>
  splitGleapHost(hostname)?.label === "staticfiles";

/**
 * Any Gleap-owned file host of any region: the public attachment CDN
 * (`staticfiles.*`) or the files service (`files.*`).
 */
export const isGleapFileHost = (hostname?: string | null): boolean => {
  const label = splitGleapHost(hostname)?.label;
  return label === "staticfiles" || label === "files";
};
